import { Injectable, TemplateRef, signal } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * The link between a `layout: 'route'` section and the route field inside it, mirroring
 * WizardFooterSlot. Provided by every section; `active` is only true in a route section, so a route
 * field anywhere else (or in a nested card section) sees `false` and renders exactly as before.
 */
@Injectable()
export class RouteSectionSlot {
  readonly #active = signal(false);
  readonly #stopRoute = signal<FormlyFieldConfig | null>(null);
  readonly #addStop = signal<TemplateRef<unknown> | null>(null);
  readonly #map = signal<TemplateRef<unknown> | null>(null);
  readonly #mapVisible = signal(false);

  /** True when the providing section has `layout: 'route'`. */
  readonly active = this.#active.asReadonly();
  /** The route field's add button, drawn by the section under the destination slot. */
  readonly addStop = this.#addStop.asReadonly();

  /** The route field's inline map (`app-route-map` with its locations), drawn by the section above the rail. */
  readonly map = this.#map.asReadonly();
  /** Whether the user opened the map. Local to the section: viewing the map is not editing, so it survives read/disabled mode. */
  readonly mapVisible = this.#mapVisible.asReadonly();

  toggleMap(): void {
    this.#mapVisible.update(v => !v);
  }

  showMap(): void {
    this.#mapVisible.set(true);
  }

  publishMap(template: TemplateRef<unknown>): void {
    this.#map.set(template);
  }

  clearMap(template: TemplateRef<unknown>): void {
    if (this.#map() !== template) return;
    this.#map.set(null);
    // A route that comes back starts closed rather than opening on its own.
    this.#mapVisible.set(false);
  }

  setActive(active: boolean): void {
    this.#active.set(active);
  }

  /** The one route the section draws as its stop list (partitionRouteSection), or null. */
  setStopRoute(field: FormlyFieldConfig | null): void {
    this.#stopRoute.set(field);
  }

  /**
   * Whether this route field is the section's stop list. Any other route that reaches this
   * instance — inside a group or repeat, or a second route — was not slotted and stays as is.
   * Compares config identity: Formly keeps a field's config object for its component's lifetime,
   * and replacing `fields` re-creates the section.
   */
  owns(field: FormlyFieldConfig | undefined): boolean {
    return this.#active() && !!field && field === this.#stopRoute();
  }

  publishAddStop(template: TemplateRef<unknown>): void {
    this.#addStop.set(template);
  }

  /** Only clears its own template: with two route fields in one section, the loser's cleanup must not remove the winner's. */
  clearAddStop(template: TemplateRef<unknown>): void {
    if (this.#addStop() === template) this.#addStop.set(null);
  }
}
